import moment from 'moment';
import { memo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { twJoin } from 'tailwind-merge';

import { PresenceEnum } from '@/models/ScheduledLessonReport';
import LessonProgressDashboardStats from '@/models/LessonProgressDashboardStats';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import Tag from '@/components/ui/custom/dataDisplay/Tag';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';

type TagInfo = {
  className: ClassNameComponent;
  icon: ReactNode;
};

type Props = {
  presence: LessonProgressDashboardStats['presenceStatus'];
  date: string;
};

const LessonProgressPresence = memo((props: Props) => {
  const { presence, date } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'presenceStatus',
  });

  const tooltip = t(presence || PresenceEnum.NONE);

  const baseClassName =
    'bg-base-100 border px-1.5 py-0.5 gap-1 pt-0 w-[5.5rem] justify-center';

  const iconClassName = 'text-base-100 w-5 h-5';

  const status: { [key in PresenceEnum]: TagInfo } = {
    ATTENDED: {
      className: {
        base: twJoin(baseClassName, 'bg-success/80 border-success'),
      },
      icon: <CheckIcon className={iconClassName} />,
    },
    MISSED: {
      className: {
        base: twJoin(baseClassName, 'bg-warning/80 border-warning'),
      },
      icon: <XMarkIcon className={iconClassName} />,
    },
    RESCHEDULED: {
      className: {
        base: twJoin(baseClassName, 'bg-info/80 border-info'),
      },
      icon: <CalendarIcon className={iconClassName} />,
    },
    NONE: {
      className: {
        base: twJoin(baseClassName, 'bg-accent/80 border-accent'),
      },
      icon: <CalendarIcon className={iconClassName} />,
    },
  };

  const info = status[presence || PresenceEnum.NONE];

  return (
    <ConditionalRenderer condition={!!date}>
      <Tooltip text={tooltip}>
        <Tag
          text={moment(date).format('DD/MM')}
          color="custom"
          icon={info.icon}
          className={{ ...info.className, text: 'text-base-100 font-400' }}
        />
      </Tooltip>
    </ConditionalRenderer>
  );
});

export default LessonProgressPresence;
